<template>
  <GoogleMapLoader
    :map-options="mapOptions"
    :api-key="$config.public.googleMapsApiKey"
  >
    <template v-if="markers" #default="{ mapsLoader, map }">
      <GoogleMapMarker
        v-for="(marker, index) in markers"
        :key="index"
        :marker-data="marker"
        :loader="mapsLoader"
        :map="map"
        @click:link="trackClick"
      />
    </template>
  </GoogleMapLoader>
</template>

<script setup lang="ts">
import { useMapMarker } from '~/composables/useMapMarker'
import { useStores } from '~/composables/useStores'
import type { IStore } from '~/rpcMethods'

type MapOptions = google.maps.MapOptions

const mapSettings = {
  clickableIcons: false,
  streetViewControl: false,
  // panControlOptions: false, @todo
  gestureHandling: 'cooperative',
  mapTypeControl: false,
  zoomControlOptions: {
    style: 'SMALL',
  },
  zoom: 7,
  minZoom: 7,
  maxZoom: 15,
} as MapOptions

const { $currentShop, $config } = useNuxtApp()
const route = useRoute()
const { stores } = useStores()
const { markers, addMarker, resetLatLngBounds } = useMapMarker()
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { trackButtonClick } = useTrackingEvents()
const trackClick = (event: MouseEvent) => {
  const element = event?.target as HTMLButtonElement
  trackButtonClick(element)
}

const isDetailsPage = computed(() => {
  return (
    typeof route?.name === 'string' && route.name.includes(routeList.store.name)
  )
})

const setMarkers = () => {
  stores.value?.forEach((store: IStore) => {
    addMarker({
      position: {
        lat: store.storeLocation.coordinates['0'],
        lng: store.storeLocation.coordinates['1'],
      },
      store,
      anchor: `store-${store.id}`,
      noClick: false,
      active: true,
    })
  })
}

onMounted(() => {
  if (!isDetailsPage.value) {
    setMarkers()
  }
})

scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(
    stores,
    () => {
      resetLatLngBounds()
      if (!isDetailsPage.value) {
        setMarkers()
      }
    },
    { immediate: true },
  )
})

const mapOptions = computed<MapOptions>(() => ({
  ...mapSettings,
  center: $currentShop.baseCoord,
}))
</script>

<style lang="scss">
button.gm-ui-hover-effect {
  top: 10px !important;
  right: 10px !important;
}
</style>
